import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { combineLatest, forkJoin, Observable } from 'rxjs';
import { map, mergeMap, share, switchMap } from 'rxjs/operators';

import { EnvironmentConfigService, PublicSettingsDataService } from '@stylehaven/components';
import { UserTokens } from '../../../common/entities/user/user-tokens.entity';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    private readonly CLIENT_ID$: Observable<string>;
    private readonly CLIENT_SECRET$: Observable<string>;

    private readonly loginUrl$: Observable<string>;
    private readonly refreshUrl$: Observable<string>;

    constructor(
        private readonly http: HttpClient,
        private readonly configService: EnvironmentConfigService,
        private publicSettingsDataService: PublicSettingsDataService
    ) {
        this.loginUrl$ = this.configService.get('authenticationUrl');

        this.refreshUrl$ = this.configService.get('authorizationUrl').pipe(map(url => `${url}auth/oauth2/token`));

        this.CLIENT_ID$ = this.configService.get('clientId');
        this.CLIENT_SECRET$ = this.configService.get('clientSecret');
    }

    public hasDirectOAuth(): Observable<boolean> {
        return this.configService.getConfig().pipe(
            map(config => {
                const autologin: string | undefined = config.directOAuth;
                return !!autologin;
            })
        );
    }

    get oauthLogoutUrl$(): Observable<string> {
        return this.configService.getConfig().pipe(
            map(config => {
                const authUrl = `${config.authorizationUrl}auth`;
                const redirectUri: string = config.baseUrl;
                return `${authUrl}/?logout&client_id=${config.clientId}&redirect_uri=${redirectUri}`;
            })
        );
    }

    public getOauthLogoutUrlWithRedirectStream(redirectUri: string): Observable<string> {
        return this.configService.getConfig().pipe(
            map(config => {
                const authUrl = `${config.authorizationUrl}auth`;
                return `${authUrl}/?logout&client_id=${config.clientId}&redirect_uri=${redirectUri}`;
            })
        );
    }

    public getOauthUrl(stateKey: string, pkceChallenge: string): Observable<string> {
        return combineLatest([
            this.configService.getConfig(),
            this.getSamlAutoRedirect(),
            this.getSamlAuthenticators()
        ]).pipe(
            map(([config, samlAutoRedirect, samlAuthenticators]) => {
                const url: string = config.authorizationUrl;
                const redirectUri: string = new URL('token', config.baseUrl).toString();

                const primarySamlAuthenticator: string | undefined =
                    samlAuthenticators.length > 0 ? samlAuthenticators[0] : undefined;

                const autologin: string | undefined = !samlAutoRedirect
                    ? undefined
                    : (primarySamlAuthenticator ?? config.directOAuth);

                const params: HttpParams = new HttpParams({
                    fromObject: {
                        client_id: config.clientId,
                        response_type: 'code',
                        redirect_uri: redirectUri,
                        state: stateKey,
                        code_challenge: pkceChallenge,
                        code_challenge_method: 'S256',
                        ...(autologin && { autologin })
                    }
                });

                return `${url}auth/?${params}`;
            })
        );
    }

    /**
     * Retrieving Saml auto redirect value from settings through portal api
     */
    private getSamlAutoRedirect(): Observable<boolean> {
        return this.publicSettingsDataService
            .getWebsiteSetting('saml_auto_redirect')
            .pipe(map(samlAutoRedirect => samlAutoRedirect === 'TRUE'));
    }

    /**
     * Retrieving Saml services value from settings through portal api
     */
    private getSamlAuthenticators(): Observable<string[]> {
        return this.publicSettingsDataService.getWebsiteSetting('saml_authenticators').pipe(
            map(samlAuthenticators => (samlAuthenticators ? JSON.parse(samlAuthenticators) : '')),
            map(samlAuthenticators => (Array.isArray(samlAuthenticators) ? samlAuthenticators : []))
        );
    }

    public login(username: string, password: string): Observable<UserTokens> {
        return forkJoin([this.CLIENT_ID$, this.CLIENT_SECRET$, this.loginUrl$]).pipe(
            mergeMap(([clientId, clientSecret, loginUrl]) => {
                const credentials: FormData = new FormData();
                credentials.append('client_id', clientId);
                credentials.append('client_secret', clientSecret);
                credentials.append('username', username);
                credentials.append('password', password);

                return this.http
                    .post<UserTokens>(loginUrl, credentials)
                    .pipe(switchMap((tokens: UserTokens) => this.refreshToken(tokens.refresh_token)));
            })
        );
    }

    public requestAccessToken(token: string, verifier: string | null): Observable<UserTokens> {
        return combineLatest([this.CLIENT_ID$, this.CLIENT_SECRET$, this.refreshUrl$]).pipe(
            switchMap(([clientId, clientSecret, refreshUrl]) => {
                const credentials: object = {
                    grant_type: 'authorization_code',
                    code: token,
                    client_id: clientId,
                    client_secret: clientSecret,
                    code_verifier: verifier
                };

                const headers: HttpHeaders = new HttpHeaders();
                headers.append('Content-Type', 'application/x-www-form-urlencoded');

                return this.http.post<UserTokens>(refreshUrl, credentials, { headers });
            })
        );
    }

    public refreshToken(refreshToken: string): Observable<UserTokens> {
        return combineLatest([this.CLIENT_ID$, this.CLIENT_SECRET$, this.refreshUrl$]).pipe(
            switchMap(([clientId, clientSecret, refreshUrl]) => {
                const credentials: object = {
                    client_id: clientId,
                    client_secret: clientSecret,
                    grant_type: 'refresh_token',
                    refresh_token: refreshToken
                };

                const headers: HttpHeaders = new HttpHeaders();
                headers.append('Content-Type', 'application/x-www-form-urlencoded');

                return this.http.post<UserTokens>(refreshUrl, credentials, { headers }).pipe(share());
            })
        );
    }
}
